/**
 * Group posts and chat author fields (namat#36 P2).
 *
 * SHARED FILE: keep this file byte-identical in namat (web) and namat-mobile
 * (src/lib/groups/groupPosts.ts in both). Pure, no Firebase imports.
 */

export interface NameSource {
  firstNameAr?: string | null;
  lastNameAr?: string | null;
  firstNameEn?: string | null;
  lastNameEn?: string | null;
  photoURL?: string | null;
}

function joinName(first?: string | null, last?: string | null): string {
  return `${first ?? ''} ${last ?? ''}`.replace(/\s+/g, ' ').trim();
}

/**
 * The name to show for a person: the Arabic name in Arabic, the English name
 * otherwise, each falling back to the other, then to `fallback`.
 */
export function localizedName(
  person: NameSource | null | undefined,
  language: string,
  fallback = '',
): string {
  const ar = joinName(person?.firstNameAr, person?.lastNameAr);
  const en = joinName(person?.firstNameEn, person?.lastNameEn);
  const preferred = language === 'ar' ? ar || en : en || ar;
  return preferred || fallback.trim();
}

export interface PostAuthorFields {
  userName: string;
  userNameAr: string;
  userNameEn: string;
  userPhotoURL: string;
}

/**
 * Author fields every group post carries, written the same way by web and
 * mobile. `userName` follows the author's UI language (legacy readers show it).
 */
export function postAuthorFields(author: NameSource, language: string): PostAuthorFields {
  const userNameAr = joinName(author.firstNameAr, author.lastNameAr);
  const userNameEn = joinName(author.firstNameEn, author.lastNameEn);
  return {
    userName: localizedName(author, language, 'User'),
    userNameAr,
    userNameEn,
    userPhotoURL: author.photoURL ?? '',
  };
}

export interface LikeState {
  likes?: number | null;
  likedBy?: readonly string[] | null;
}

export function hasLiked(post: LikeState, userId: string | null | undefined): boolean {
  return !!userId && Array.isArray(post.likedBy) && post.likedBy.includes(userId);
}

/**
 * Per-member like toggle. Likes from before likedBy existed stay counted; a
 * member can only remove their own like, so the count never drops below 0.
 */
export function toggleLike(
  post: LikeState,
  userId: string,
): { likes: number; likedBy: string[]; liked: boolean } {
  const likedBy = Array.isArray(post.likedBy) ? [...post.likedBy] : [];
  const current = Math.max(
    typeof post.likes === 'number' && Number.isFinite(post.likes) ? Math.floor(post.likes) : 0,
    likedBy.length,
  );
  if (likedBy.includes(userId)) {
    return {
      likes: Math.max(0, current - 1),
      likedBy: likedBy.filter((id) => id !== userId),
      liked: false,
    };
  }
  return { likes: current + 1, likedBy: [...likedBy, userId], liked: true };
}
